'use client'

import * as Clerk from '@clerk/nextjs'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@/components/icon'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { isSatelliteHost } from './domain'
import { buildPrimaryAuthUrl } from './primary-auth'

const CLERK_UI_GRACE_MS = 450

type SignInModalProps = {
  isOpen: boolean
  onClose: () => void
}

export function SignInModal({ isOpen, onClose }: SignInModalProps) {
  const { isSignedIn, isLoaded } = Clerk.useAuth()
  const [mounted, setMounted] = useState(false)
  const [isFinishing, setIsFinishing] = useState(false)
  const [isGraceLoading, setIsGraceLoading] = useState(false)
  const didNavigateRef = useRef(false)
  const graceTimerRef = useRef<number | null>(null)
  const host = mounted ? window.location.host : ''
  const afterAuthUrl = '/auth-sync?redirect_url=/browse'
  const isBlocked = isFinishing || !isLoaded || isGraceLoading

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (isBlocked) return
      if (e.key === 'Escape') onClose()
    },
    [isBlocked, onClose],
  )

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!isOpen) {
      setIsFinishing(false)
      setIsGraceLoading(false)
      didNavigateRef.current = false
      if (graceTimerRef.current !== null) {
        window.clearTimeout(graceTimerRef.current)
        graceTimerRef.current = null
      }
      return
    }

    setIsGraceLoading(true)
    graceTimerRef.current = window.setTimeout(() => {
      setIsGraceLoading(false)
      graceTimerRef.current = null
    }, CLERK_UI_GRACE_MS)

    return () => {
      if (graceTimerRef.current !== null) {
        window.clearTimeout(graceTimerRef.current)
        graceTimerRef.current = null
      }
    }
  }, [isOpen])

  useEffect(() => {
    if (!mounted) return
    if (!isOpen) return
    if (!isSignedIn) return
    if (new URL(window.location.href).pathname === '/auth-sync') return
    if (didNavigateRef.current) return
    didNavigateRef.current = true
    setIsFinishing(true)
    onClose()
    requestAnimationFrame(() => {
      window.location.assign(afterAuthUrl)
    })
  }, [afterAuthUrl, isOpen, isSignedIn, mounted, onClose])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    if (isBlocked) return
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [handleEscape, isBlocked, isOpen])

  useEffect(() => {
    if (!mounted || !isOpen) return

    if (
      !isSatelliteHost(host, process.env.NODE_ENV === 'development' ? 'development' : 'production')
    ) {
      return
    }

    const absoluteReturnTo = new URL(afterAuthUrl, window.location.origin).toString()
    const url = buildPrimaryAuthUrl({
      action: 'sign-in',
      redirectUrl: absoluteReturnTo,
    })

    window.location.assign(url)
  }, [afterAuthUrl, host, isOpen, mounted])

  if (!isOpen || !mounted) return null

  if (
    isSatelliteHost(host, process.env.NODE_ENV === 'development' ? 'development' : 'production')
  ) {
    return null
  }

  return createPortal(
    <div className='fixed inset-0 z-9999 flex items-center justify-center bg-black/70 backdrop-blur-sm'>
      <div className='relative min-h-[560px] w-[min(460px,92vw)]'>
        <Button
          variant='ghost'
          size='icon'
          onClick={() => {
            if (isBlocked) return
            onClose()
          }}
          disabled={isBlocked}
          className='absolute -top-12 right-0 text-white hover:bg-white/10'
        >
          <Icon name='close' className='size-6' />
        </Button>
        {isBlocked && (
          <div className='absolute inset-0 z-10 flex items-center justify-center bg-black/70 px-8 py-10 text-center text-white'>
            <div className='flex flex-col items-center justify-center gap-3'>
              <Spinner className='size-6' />
              <p className='font-medium text-sm uppercase tracking-[0.06rem] opacity-90'>
                {isFinishing ? 'Finishing setup...' : 'Loading...'}
              </p>
            </div>
          </div>
        )}
        <div className={isBlocked ? 'pointer-events-none select-none opacity-70' : undefined}>
          <Clerk.SignIn
            routing='hash'
            fallbackRedirectUrl={afterAuthUrl}
            signUpFallbackRedirectUrl={afterAuthUrl}
          />
        </div>
      </div>
    </div>,
    document.body,
  )
}
